import {
  AppType,
  CommonMessageType,
  sendElectronMessage,
} from 'electron-communication-lib/CommonForApp';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ChevronSmallLeft} from '@ELib/assets/images';
import {EInfobox, EWarningBox} from '@ELib/components/EBox';
import {ESubmitButton, EUndecoratedButton} from '@ELib/components/EButtons';
import {EFade} from '@ELib/components/EFade';
import {ELink} from '@ELib/components/ELink';
import {ECheckbox} from '@ELib/components/form/ECheckbox';
import {EForm, EFormButtonRow} from '@ELib/components/form/EForm';
import {EPasswordField} from '@ELib/components/form/EPasswordField';
import {ETextInput} from '@ELib/components/form/ETextInput';
import {StyledGotoServerInstance} from '@ELib/designComponents/Signin/Signin.styles';
import {isElectron} from '@ELib/utilities/HelperFunctions';
import {
  clearUserData,
  getShouldStorePassword as getShouldStorePasswordLS,
  getStoredUsername,
  setShouldStorePassword as setShouldStorePasswordLS,
  setUserData,
} from '@ELib/utilities/LocalStorage';
import {useAppType} from '@ELib/utilities/hooks/appType';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';
import {useCommonMessageExchange} from '@ELib/utilities/hooks/useElectronMessageExchange';
import {resetPasswordURLString} from '@ELib/utilities/resetPasswordURLString';

import {ContentWrapper, GroupWrapper, TextButton} from '../sharedStyles/login';

import {
  checkPW,
  initAuthSession,
  setLocalBaseURL,
  setLocalSessionType,
  useKeyChainResponse,
} from './loginLogic';

// needs to be reworked anyway
// eslint-disable-next-line max-statements
export const Signin = ({
  onLoad,
  onSuccess,
  onError,
  baseURL,
  sessionType = 'default-application',
  onPWReset,
}: {
  onLoad?: () => void;
  onSuccess: (data?: unknown) => void;
  onError?: (err?: Error | string) => void;
  baseURL?: string;
  sessionType?: string;
  onPWReset?: (username: string) => void;
}): React.ReactElement => {
  const [error, setError] = useState<string>();
  const [showError, setShowError] = useState(false);
  const [username, setUsername] = useState(getStoredUsername() || '');
  const [password, setPassword] = useState('');
  const [instanceName, setInstanceName] = useState('');

  const [shouldSaveUser, setShouldSaveUser] = useState(
    Boolean(getStoredUsername())
  );
  const [shouldSavePassword, setShouldSavePassword] = useState(
    getShouldStorePasswordLS()
  );

  const [showRedirectMessage, setShowRedirectMessage] =
    useState<boolean>(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [multipleInstances, setMultipleInstances] = useState(false);

  const {t} = useTranslation('lib');

  if (baseURL) {
    setLocalBaseURL(baseURL);
  }

  if (sessionType) {
    setLocalSessionType(sessionType);
  }

  const handleLoginError = (err: string) => {
    setError(t(err));
    onError?.(err);

    setIsLoggingIn(false);

    setShowError(true);
  };

  const login = async () => {
    setShowError(false);

    // make sure no left over cookies are present when logging in
    sendElectronMessage({
      type: CommonMessageType.CLEAR_ELECTRON_COOKIES,
      payload: null,
    });

    setIsLoggingIn(true);

    const initAuthStatus = await initAuthSession();

    switch (initAuthStatus) {
      case 200:
        onSuccess();
        return;
      case 401:
        break;
      default:
        handleLoginError('login.warnings.loginNotPossible');
        return;
    }

    const {ok, code} = await checkPW(username, password);

    setIsLoggingIn(false);

    // These error codes are document on https://docs.airlock.com/iam/8.0/loginapp-rest/index.html
    // under 1.2.8 Response format -> error codes
    if (!ok) {
      if (code === 'USER_LOCKED') {
        handleLoginError('login.warnings.userLocked');
      } else if (code === 'USERNAME_PASSWORD_WRONG') {
        handleLoginError('login.warnings.loginFailed');
      } else {
        handleLoginError('login.warnings.loginNotPossible');
      }
      return;
    } else if (isElectron()) {
      sendElectronMessage({
        type: CommonMessageType.SAVE_LOGIN_CREDENTIALS,
        payload: {
          username,
          password: shouldSavePassword ? password : null,
        },
      });
    }

    if (shouldSaveUser) {
      setUserData(username);
      setShouldStorePasswordLS(shouldSavePassword);
    } else {
      clearUserData();
      setShouldStorePasswordLS(false);
    }

    onSuccess();
  };

  const appType = useAppType();

  useMountEffect(() => {
    (async () => {
      try {
        if (!(await initAuthSession())) {
          handleLoginError('login.warnings.authNotPossible');
        }
      } catch (err) {
        // Notify auth creation failure
        // eslint-disable-next-line no-console
        console.error('Failed to create auth session', err);
      }
    })();

    const urlParams = new URLSearchParams(window.location.search);

    setShowRedirectMessage(urlParams.has('redirect'));
  });

  // Check if electron has the login saved in a user keychain
  useKeyChainResponse(onSuccess);

  useMountEffect(() => {
    onLoad?.();
  });

  const validInput = username !== '' && password !== '';

  useCommonMessageExchange({
    message: {type: CommonMessageType.REQUEST_INSTANCES, payload: null},
    responsePayloadHandler: (responsePayload: string[]) => {
      setMultipleInstances(responsePayload.length > 1);
    },
  });

  useCommonMessageExchange({
    message: {type: CommonMessageType.GET_CURRENT_INSTANCE, payload: null},
    responsePayloadHandler: (response: {url?: string; name?: string}) => {
      setInstanceName(response.name || window.location.host);
    },
  });

  const GotoServerInstance = useCallback(() => {
    const gotoInstanceSelection = () => {
      sendElectronMessage({
        type: CommonMessageType.LOAD_INSTANCE_SELECTOR,
        payload: null,
      });
    };

    return multipleInstances ? (
      <StyledGotoServerInstance>
        <EUndecoratedButton onClick={gotoInstanceSelection}>
          <ChevronSmallLeft /> {t('login.actions.toServerSelection')}
        </EUndecoratedButton>
      </StyledGotoServerInstance>
    ) : null;
  }, [multipleInstances, t]);

  return (
    <ContentWrapper>
      <GotoServerInstance />
      <GroupWrapper>
        {instanceName ? (
          <EInfobox>{t('login.info.instance', {instanceName})}</EInfobox>
        ) : null}
        {showRedirectMessage ? (
          <EInfobox>{t('login.info.sessionTimedOut')}</EInfobox>
        ) : null}
      </GroupWrapper>
      <EForm
        onSubmit={() => {
          login();
        }}
      >
        <GroupWrapper>
          <ETextInput
            autoFocus={!username}
            label={t('login.inputs.username')}
            name='username'
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          <EPasswordField
            autoComplete='new-password' /* This is a necessary workaround as chrome ignores autoComplete="off" */
            autoFocus={Boolean(username)}
            label={t('login.inputs.password')}
            name='password'
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </GroupWrapper>
        {showError ? (
          <EFade in={showError}>
            <EWarningBox>{error}</EWarningBox>
          </EFade>
        ) : null}
        <EFormButtonRow>
          {appType === AppType.LEXIAN_BROWSER ||
          appType === AppType.WEB_BROWSER ? (
            <div>
              <ECheckbox
                label={t('login.actions.saveUser')}
                value={shouldSaveUser}
                onChange={(value) => {
                  setShouldSaveUser(value);
                  if (!value) {
                    setShouldSavePassword(false);
                  }
                }}
              />
              {appType === AppType.LEXIAN_BROWSER ? (
                <ECheckbox
                  disabled={!shouldSaveUser}
                  label={t('login.actions.savePassword')}
                  value={shouldSavePassword}
                  onChange={setShouldSavePassword}
                />
              ) : null}
            </div>
          ) : null}
          {appType === AppType.LEXIAN_TIME_TRACKER ? (
            <ECheckbox
              label={t('login.actions.saveUserAndPassword')}
              value={shouldSaveUser}
              onChange={(value) => {
                setShouldSaveUser(value);
                setShouldSavePassword(value);
              }}
            />
          ) : null}
          <ESubmitButton
            disabled={isLoggingIn || !validInput}
            isSubmitting={isLoggingIn}
            testId='button-login'
          >
            {t('login.actions.login')}
          </ESubmitButton>
        </EFormButtonRow>
      </EForm>
      <div style={{width: 'fit-content', alignSelf: 'center'}}>
        {onPWReset ? (
          <TextButton onClick={() => onPWReset(username)}>
            {t('login.actions.forgotPassword')}
          </TextButton>
        ) : (
          <ELink href={resetPasswordURLString(username || '')}>
            {t('login.actions.forgotPassword')}
          </ELink>
        )}
      </div>
    </ContentWrapper>
  );
};
